import React, { useState } from 'react';
import { PagePlanV2, PagePlanSectionV2 } from '../../types/content-strategy-v2.type';
import { Chip, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';

interface PagePlanDeckProps {
  pagePlan: PagePlanV2;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}

export const PagePlanDeck: React.FC<PagePlanDeckProps> = ({
  pagePlan,
  onRegenerate,
  isRegenerating = false
}) => {
  const [sections, setSections] = useState<PagePlanSectionV2[]>(
    pagePlan.sections || []
  );
  const [editingSectionIdx, setEditingSectionIdx] = useState<number | null>(null);

  const handleUpdateSectionTitle = (idx: number, title: string) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], title };
    setSections(updated);
  };

  const handleUpdateSectionPurpose = (idx: number, purpose: string) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], purpose };
    setSections(updated);
  };

  const handleUpdateSectionBullets = (idx: number, bulletsText: string) => {
    const updated = [...sections];
    updated[idx] = {
      ...updated[idx],
      bullets: bulletsText.split('\n').filter((b) => b.trim().length > 0)
    };
    setSections(updated);
  };

  return (
    <div className="flex flex-col gap-4 mt-4">
      {/* Header & Regenerate button */}
      <div className="flex justify-between items-center bg-[#F9FAFB] p-3 rounded-lg border border-[#EAECF0]">
        <span className="text-xs font-semibold text-[#344054] uppercase tracking-wider">
          📐 AI Page Plan Blueprint
        </span>
        {onRegenerate && (
          <button
            type="button"
            onClick={onRegenerate}
            disabled={isRegenerating}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#0D6F66] hover:text-[#094d47] bg-transparent border-none cursor-pointer p-0 disabled:opacity-50"
          >
            <RefreshIcon
              sx={{ fontSize: 16 }}
              className={isRegenerating ? 'animate-spin' : ''}
            />
            <span>{isRegenerating ? 'Regenerating...' : 'Regenerate Plan'}</span>
          </button>
        )}
      </div>

      {/* 1. Hero Card */}
      {pagePlan.hero && (
        <div className="border border-[#EAECF0] rounded-xl p-4 bg-white shadow-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-[#1D2939] uppercase tracking-wider">
              1. Hero Section
            </span>
            <Chip
              label="Above the Fold"
              size="small"
              className="!bg-[#E6F9F7] !text-[#0D6F66] !text-[11px] !font-medium"
            />
          </div>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-xs font-semibold text-[#667085] uppercase block">
                Headline (H1)
              </span>
              <p className="font-semibold text-[#1D2939] text-base mt-0.5 mb-1">
                {pagePlan.hero.headline}
              </p>
            </div>
            {pagePlan.hero.subheadline && (
              <div>
                <span className="text-xs font-semibold text-[#667085] uppercase block">
                  Subheadline
                </span>
                <p className="text-[#344054] mt-0.5 mb-1">
                  {pagePlan.hero.subheadline}
                </p>
              </div>
            )}
            {pagePlan.hero.cta && (
              <div>
                <span className="text-xs font-semibold text-[#667085] uppercase block">
                  Primary Call to Action
                </span>
                <span className="inline-block bg-[#0F0A3C] text-white text-xs font-semibold px-3 py-1.5 rounded-lg mt-1">
                  {pagePlan.hero.cta}
                </span>
              </div>
            )}
            {pagePlan.hero.visual_notes && (
              <div className="mt-2 p-2.5 bg-[#F9FAFB] rounded-lg border border-dashed border-[#D0D5DD]">
                <span className="text-xs font-semibold text-[#475467] block mb-1">
                  Visual / Media Guidance
                </span>
                <p className="text-xs text-[#667085] m-0">
                  {pagePlan.hero.visual_notes}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 2. Structured Sections */}
      {sections.length > 0 && (
        <div className="border border-[#EAECF0] rounded-xl p-4 bg-white shadow-xs">
          <span className="text-xs font-bold text-[#1D2939] uppercase tracking-wider block mb-3">
            2. Page Content Flow ({sections.length} Sections)
          </span>

          <div className="space-y-3">
            {sections.map((sec, idx) => {
              const isEditing = editingSectionIdx === idx;
              return (
                <div
                  key={idx}
                  className="border border-[#F2F4F7] rounded-lg p-3.5 bg-[#FCFCFD] hover:border-[#D0D5DD] transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      {isEditing ? (
                        <input
                          type="text"
                          value={sec.title}
                          onChange={(e) =>
                            handleUpdateSectionTitle(idx, e.target.value)
                          }
                          className="w-full text-sm font-semibold text-[#1D2939] border border-[#D0D5DD] rounded px-2 py-1 focus:outline-none focus:border-[#0F0A3C]"
                        />
                      ) : (
                        <p className="text-sm font-semibold text-[#1D2939] m-0">
                          {idx + 1}. {sec.title}
                        </p>
                      )}
                      {isEditing ? (
                        <input
                          type="text"
                          value={sec.purpose}
                          onChange={(e) =>
                            handleUpdateSectionPurpose(idx, e.target.value)
                          }
                          placeholder="Purpose of section..."
                          className="w-full text-xs text-[#667085] border border-[#D0D5DD] rounded px-2 py-1 mt-1 focus:outline-none"
                        />
                      ) : (
                        sec.purpose && (
                          <p className="text-xs text-[#667085] m-0 mt-0.5">
                            {sec.purpose}
                          </p>
                        )
                      )}
                    </div>
                    <Tooltip title={isEditing ? 'Save' : 'Edit inline'}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setEditingSectionIdx(isEditing ? null : idx)
                        }
                        sx={{ ml: 1, color: isEditing ? '#027A48' : '#667085' }}
                      >
                        {isEditing ? (
                          <CheckIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <EditIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </div>

                  {isEditing ? (
                    <div className="mt-2">
                      <span className="text-[11px] font-semibold text-[#475467] block mb-1">
                        Key Points (one per line):
                      </span>
                      <textarea
                        rows={3}
                        value={sec.bullets?.join('\n') || ''}
                        onChange={(e) =>
                          handleUpdateSectionBullets(idx, e.target.value)
                        }
                        className="w-full text-xs text-[#344054] border border-[#D0D5DD] rounded p-2 focus:outline-none focus:border-[#0F0A3C]"
                      />
                    </div>
                  ) : (
                    sec.bullets &&
                    sec.bullets.length > 0 && (
                      <ul className="text-xs text-[#475467] list-disc list-inside space-y-1 m-0 pl-1 mt-2">
                        {sec.bullets.map((b, bIdx) => (
                          <li key={bIdx}>{b}</li>
                        ))}
                      </ul>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 3. SEO Card */}
      {pagePlan.seo && (
        <div className="border border-[#EAECF0] rounded-xl p-4 bg-white shadow-xs">
          <span className="text-xs font-bold text-[#1D2939] uppercase tracking-wider block mb-3">
            3. On-Page SEO Specifications
          </span>
          <div className="space-y-2.5 text-xs">
            <div>
              <span className="font-semibold text-[#667085] uppercase block">
                Meta Title
              </span>
              <p className="text-sm font-medium text-[#1D2939] mt-0.5">
                {pagePlan.seo.meta_title || '—'}
              </p>
            </div>
            <div>
              <span className="font-semibold text-[#667085] uppercase block">
                Meta Description
              </span>
              <p className="text-xs text-[#475467] mt-0.5 leading-relaxed">
                {pagePlan.seo.meta_description || '—'}
              </p>
            </div>
            {pagePlan.seo.h1 && (
              <div>
                <span className="font-semibold text-[#667085] uppercase block">
                  Target H1 Tag
                </span>
                <p className="text-xs font-medium text-[#1D2939] mt-0.5">
                  {pagePlan.seo.h1}
                </p>
              </div>
            )}
            {pagePlan.seo.keywords && pagePlan.seo.keywords.length > 0 && (
              <div>
                <span className="font-semibold text-[#667085] uppercase block mb-1.5">
                  Target Keywords
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {pagePlan.seo.keywords.map((kw, idx) => (
                    <Chip
                      key={idx}
                      label={kw}
                      size="small"
                      className="!bg-[#F2F4F7] !text-[#344054] !text-xs !font-medium"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 4. Design Notes Card */}
      {pagePlan.design_notes && pagePlan.design_notes.length > 0 && (
        <div className="border border-[#EAECF0] rounded-xl p-4 bg-white shadow-xs">
          <span className="text-xs font-bold text-[#1D2939] uppercase tracking-wider block mb-2">
            4. UX & Design Guidelines
          </span>
          <ul className="text-xs text-[#475467] list-disc list-inside space-y-1 m-0 pl-1">
            {pagePlan.design_notes.map((note, idx) => (
              <li key={idx}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/* 5. Internal Links Card */}
      {pagePlan.internal_links && pagePlan.internal_links.length > 0 && (
        <div className="border border-[#EAECF0] rounded-xl p-4 bg-white shadow-xs">
          <span className="text-xs font-bold text-[#1D2939] uppercase tracking-wider block mb-2">
            5. Recommended Internal Linking
          </span>
          <div className="flex flex-wrap gap-2">
            {pagePlan.internal_links.map((link, idx) => (
              <div
                key={idx}
                className="border border-[#EAECF0] rounded-lg px-3 py-1.5 bg-[#F9FAFB] text-xs flex items-center gap-2"
              >
                <span className="font-medium text-[#1D2939]">"{link.anchor}"</span>
                <span className="text-[#667085]">➔ {link.target_url}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PagePlanDeck;
